import React, { useMemo } from 'react';
import {
  Box,
  ButtonBase,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  eachUtcDayKeyInRange,
  formatUtcDayLabel,
  formatUtcDayLabelCompact,
  buildEvenChartTickIndices,
  CHART_TREND_UNAVAILABLE,
} from './analyticsChartDays';
import { ANALYTICS_CHART_TICK, ANALYTICS_HEADLINE_NUMBER_SX } from './analyticsTypography';
import { showUniqueVisitorsTrendChart } from './analyticsThresholds';
import { countUniqueVisitors as countUniqueVisitorsFromRows, visitorKeyForRow } from './visitorIdentity';

const VisitorTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const dateLabel = row?.date ? formatUtcDayLabel(row.date) : '';
  const visitors = payload.find((p) => p.dataKey === 'visitors')?.value ?? 0;
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '8px',
        p: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(0,0,0,0.7)', mb: 0.25 }}>
        {dateLabel}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.6)' }}>
        {visitors} unique visitor{visitors !== 1 ? 's' : ''}
      </Typography>
    </Box>
  );
};

export const useVisitorTimeseries = (
  feedRows,
  periodFrom,
  periodTo,
  { allRows = null, directory = null } = {}
) =>
  useMemo(() => {
    if (!periodFrom || !periodTo) return [];

    const dayMap = new Map();
    const identityRows = allRows || feedRows;

    if (Array.isArray(feedRows)) {
      feedRows.forEach((row) => {
        if (row?.meta?.event !== 'webview_opened') return;

        const ts = row?.timestamp;
        if (!ts) return;
        const date = new Date(ts).toISOString().slice(0, 10);

        if (!dayMap.has(date)) {
          dayMap.set(date, { visitors: new Set() });
        }
        const bucket = dayMap.get(date);

        const key = visitorKeyForRow(row, identityRows, directory);
        if (key) bucket.visitors.add(key);
      });
    }

    return eachUtcDayKeyInRange(periodFrom, periodTo).map((date) => ({
      date,
      label: formatUtcDayLabelCompact(date),
      visitors: dayMap.get(date)?.visitors?.size ?? 0,
    }));
  }, [feedRows, periodFrom, periodTo, allRows, directory]);

/** @deprecated Import from ./visitorIdentity — re-exported for existing tests. */
export const countUniqueVisitors = (feedRows, opts) =>
  countUniqueVisitorsFromRows(feedRows, opts);

const UniqueVisitorsCard = ({
  feedRows = [],
  loading = false,
  visitorStats = null,
  audienceFiltered = false,
  feedFetchSettled = false,
  onClick = null,
  periodFrom = null,
  periodTo = null,
  feedCapped = false,
  timeRangeLabel = null,
  identityContextRows = null,
  userDirectory = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const timeseries = useVisitorTimeseries(feedRows, periodFrom, periodTo, {
    allRows: identityContextRows,
    directory: userDirectory,
  });

  const computedUnique = useMemo(
    () =>
      countUniqueVisitorsFromRows(feedRows, {
        allRows: identityContextRows || feedRows,
        directory: userDirectory,
      }),
    [feedRows, identityContextRows, userDirectory]
  );

  const hasAnyGalleryViews = useMemo(() => {
    if (!Array.isArray(feedRows)) return false;
    return feedRows.some((r) => r?.meta?.event === 'webview_opened');
  }, [feedRows]);

  const apiUnique =
    !audienceFiltered &&
    visitorStats?.uniqueVisitors != null &&
    visitorStats.uniqueVisitors > 0
      ? visitorStats.uniqueVisitors
      : null;

  const displayUnique =
    feedFetchSettled || audienceFiltered
      ? computedUnique ?? 0
      : computedUnique ?? apiUnique;
  const hasTrendData = timeseries.some((d) => d.visitors > 0);
  const hasChart =
    !feedCapped &&
    timeseries.length >= 2 &&
    hasTrendData &&
    showUniqueVisitorsTrendChart(displayUnique ?? 0);
  const chartMax = useMemo(
    () => Math.max(1, ...timeseries.map((d) => d.visitors)),
    [timeseries]
  );
  const chartData = useMemo(
    () => timeseries.map((row, idx) => ({ ...row, idx })),
    [timeseries]
  );
  const xTickIndices = useMemo(
    () => buildEvenChartTickIndices(timeseries.length, isMobile ? 4 : 5),
    [timeseries.length, isMobile]
  );
  const periodSubtitle = timeRangeLabel
    ? `in the ${timeRangeLabel.toLowerCase()}`
    : 'in this period';
  // Drop the literal "0" baseline tick so the axis reads cleanly. Show only
  // mid + max so the line sits in a labelled range without zero noise.
  const yAxisTicks = useMemo(() => {
    if (chartMax <= 0) return [];
    if (chartMax === 1) return [1];
    const mid = Math.max(1, Math.round(chartMax / 2));
    return mid === chartMax ? [chartMax] : [mid, chartMax];
  }, [chartMax]);

  const canViewDrilldown = typeof onClick === 'function';
  const CardShell = hasChart ? Box : ButtonBase;
  const cardShellSx = {
    position: 'relative',
    width: '100%',
    borderRadius: hasChart ? '14px' : '14px !important',
    border: '1px solid rgba(0,0,0,0.06)',
    bgcolor: '#fff',
    p: { xs: 1.75, sm: 2 },
    ...(hasChart
      ? {}
      : {
          textAlign: 'left',
          display: 'block',
          transition: 'background 160ms ease, border-color 160ms ease, transform 120ms ease',
          '&:hover': canViewDrilldown
            ? { bgcolor: 'rgba(0,0,0,0.015)', borderColor: 'rgba(0,0,0,0.1)' }
            : {},
          '&:active': canViewDrilldown ? { transform: 'scale(0.997)' } : {},
          cursor: canViewDrilldown ? 'pointer' : 'default',
          textTransform: 'none',
          '& .MuiTypography-root': { textTransform: 'none' },
          opacity: canViewDrilldown ? 1 : 0.5,
        }),
  };
  const cardShellProps = hasChart
    ? {}
    : {
        onClick,
        disabled: !canViewDrilldown,
      };

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={148}
        sx={{ borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.04)' }}
      />
    );
  }

  return (
    <CardShell {...cardShellProps} sx={cardShellSx}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ mb: { xs: 1, sm: 1.25 }, pr: hasChart ? 0 : 3 }}
      >
        <GroupOutlinedIcon sx={{ fontSize: 15, color: 'rgba(0,0,0,0.4)', flexShrink: 0 }} />
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', md: '0.9375rem' },
            fontWeight: 500,
            color: 'rgba(0,0,0,0.5)',
          }}
        >
          Unique Visitors
        </Typography>
      </Stack>

      {displayUnique != null && (
        <>
          <Typography sx={ANALYTICS_HEADLINE_NUMBER_SX}>
            {displayUnique.toLocaleString()}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.75rem', md: '0.8125rem' },
              fontWeight: 400,
              color: 'rgba(0,0,0,0.45)',
              mt: 0.3,
              mb: hasChart ? { xs: 1.25, sm: 1.5 } : 0,
            }}
          >
            {periodSubtitle}
          </Typography>
        </>
      )}

      {displayUnique == null && hasAnyGalleryViews && (
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: 'rgba(0,0,0,0.42)',
            lineHeight: 1.5,
            mb: hasChart ? { xs: 1.25, sm: 1.5 } : 0,
            maxWidth: 360,
          }}
        >
          Floor plan links were opened in this period, but we could not tie them to
          individual visitors yet.
        </Typography>
      )}

      {feedCapped && (
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: 'rgba(0,0,0,0.42)',
            lineHeight: 1.5,
            mb: hasChart ? { xs: 1, sm: 1.25 } : 0,
            maxWidth: 420,
          }}
        >
          {CHART_TREND_UNAVAILABLE}
        </Typography>
      )}

      {hasChart ? (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 4, left: -6, bottom: 0 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="rgba(0,0,0,0.06)"
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="idx"
              type="number"
              domain={[0, Math.max(0, timeseries.length - 1)]}
              ticks={xTickIndices}
              tickFormatter={(idx) => timeseries[Number(idx)]?.label ?? ''}
              tick={ANALYTICS_CHART_TICK}
              axisLine={false}
              tickLine={false}
              padding={{ left: 0, right: 18 }}
            />
            <YAxis
              allowDecimals={false}
              domain={[0, chartMax]}
              ticks={yAxisTicks}
              tick={ANALYTICS_CHART_TICK}
              axisLine={false}
              tickLine={false}
              width={24}
            />
            <RechartsTooltip
              content={<VisitorTooltip />}
              cursor={{ stroke: 'rgba(0,0,0,0.06)', strokeWidth: 1 }}
            />
            <Line
              type="monotone"
              dataKey="visitors"
              stroke="rgba(0,0,0,0.72)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: 'rgba(0,0,0,0.8)' }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : null}

      {hasChart ? (
        <ButtonBase
          onClick={onClick}
          disabled={!canViewDrilldown}
          aria-label="See activity"
          sx={{
            mt: { xs: 1.5, sm: 2 },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.25,
            py: 1,
            borderRadius: '8px !important',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: 'rgba(0,0,0,0.015)',
            textTransform: 'none !important',
            transition: 'background 160ms ease',
            '&:hover': canViewDrilldown ? { bgcolor: 'rgba(0,0,0,0.035)' } : {},
            opacity: canViewDrilldown ? 1 : 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              color: 'rgba(0,0,0,0.75)',
            }}
          >
            See activity
          </Typography>
          <ChevronRight sx={{ fontSize: 18, color: 'rgba(0,0,0,0.4)' }} />
        </ButtonBase>
      ) : (
        <ChevronRight
          sx={{
            position: 'absolute',
            top: { xs: 15, sm: 19 },
            right: { xs: 11, sm: 15 },
            fontSize: 18,
            color: 'rgba(0,0,0,0.3)',
            opacity: 0.45,
            pointerEvents: 'none',
          }}
        />
      )}
    </CardShell>
  );
};

export default UniqueVisitorsCard;
